import i18n from 'i18next';
import {initReactI18next} from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import englishTranslations from './locales/english.json';
import polishTranslations from './locales/polish.json';
import germanTranslations from './locales/german.json';
import frenchTranslations from './locales/french.json';
import italianTranslations from './locales/italian.json';
import {CONFIG} from '../config/settings';

const STORAGE_KEY = 'language';
export const DEFAULT_LANGUAGE: string = CONFIG.i18n.defaultLanguage;
const SUPPORTED_LANGUAGES: string[] = CONFIG.i18n.supportedLanguages as unknown as string[];

// Helper function to get supported languages for UI components
export const getSupportedLanguages = (): string[] => {
  return SUPPORTED_LANGUAGES;
};

const isSupported = (lang: string | null): lang is string => {
  return lang !== null && SUPPORTED_LANGUAGES.includes(lang);
};

const getLanguageFromUrl = (): string | undefined => {
  const urlParams = new URLSearchParams(window.location.search);
  const langParam = urlParams.get('lang');
  if (isSupported(langParam)) {
    localStorage.setItem(STORAGE_KEY, langParam);
    return langParam;
  }
  return undefined;
};

const getLanguageFromStorage = (): string | undefined => {
  const stored = localStorage.getItem(STORAGE_KEY);
  return isSupported(stored) ? stored : undefined;
};

const getLanguageFromBrowser = (): string | undefined => {
  const browserLang = navigator.language.toLowerCase();
  return SUPPORTED_LANGUAGES.find(lang => browserLang.startsWith(lang));
};

const customDetector = {
  name: 'customDetector',
  lookup(): string | undefined {
    // URL query parameter
    const fromUrl = getLanguageFromUrl();
    if (fromUrl) return fromUrl;

    // localStorage
    const fromStorage = getLanguageFromStorage();
    if (fromStorage) return fromStorage;

    // Browser settings
    const fromBrowser = getLanguageFromBrowser();
    if (fromBrowser) return fromBrowser;

    // Default fallback
    return DEFAULT_LANGUAGE;
  },
  cacheUserLanguage(lng: string): void {
    if (isSupported(lng)) {
      localStorage.setItem(STORAGE_KEY, lng);
    }
  }
};

const languageDetector = new LanguageDetector();
languageDetector.addDetector(customDetector);

i18n
  .use(languageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      pl: {
        translation: polishTranslations
      },
      en: {
        translation: englishTranslations
      },
      de: {
        translation: germanTranslations
      },
      fr: {
        translation: frenchTranslations
      },
      it: {
        translation: italianTranslations
      }
    },
    fallbackLng: DEFAULT_LANGUAGE,
    supportedLngs: SUPPORTED_LANGUAGES,
    detection: {
      order: ['customDetector'],
      caches: ['customDetector']
    },
    interpolation: {
      escapeValue: false
    }
  });

// Update URL query parameter when language changes
i18n.on('languageChanged', (lng: string) => {
  if (isSupported(lng)) {
    const url = new URL(window.location.href);
    url.searchParams.set('lang', lng);
    window.history.replaceState({}, '', url.toString());
  }
});

export default i18n;

